import { defineStore } from 'pinia'
import { authApiClient } from '@/services/axios'
import { cookieUtils } from '@/utils/cookies'
import { deviceLogsService } from '@/services/deviceLogsService'
import type { StrapiUser, AuthState, ApiError } from '@/types/auth.types'

// Shared while the initial user load is in flight, so concurrent callers wait for the same request
let initializeAuthPromise: Promise<unknown> | null = null

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => {
    // Initialize from cookies immediately when store is created
    const role = cookieUtils.getUserRole()
    const city = cookieUtils.getUserCity()

    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      role,
      city,
    }
  },

  getters: {
    isLoggedIn: (state) => !!state.user && state.isAuthenticated,

    getToken: () => cookieUtils.getJWTToken(),

    hasValidToken: () => !!cookieUtils.getJWTToken(),

    isContentManager: (state) => state.role === 'content-manager',

    isAdminManager: (state) => state.role === 'admin-manager',

    userCityDocumentId: (state) => {
      // For content managers and admin managers, use the resource center's city
      if ((state.role === 'content-manager' || state.role === 'admin-manager') && state.user?.resource_center?.city?.documentId) {
        return state.user.resource_center.city.documentId
      }
      // For regular users, use the user's city
      return state.city?.documentId || null
    },

    userCityId: (state) => {
      // For content managers and admin managers, use the resource center's city
      if ((state.role === 'content-manager' || state.role === 'admin-manager') && state.user?.resource_center?.city?.id) {
        return state.user.resource_center.city.id
      }
      // For regular users, use the user's city
      return state.city?.id || null
    },

    selectedCityForDisplay: (state) => {
      // For content managers and admin managers, use the resource center's city
      if ((state.role === 'content-manager' || state.role === 'admin-manager') && state.user?.resource_center?.city) {
        return {
          id: state.user.resource_center.city.id.toString(),
          documentId: state.user.resource_center.city.documentId,
          name: state.user.resource_center.city.title,
          label: state.user.resource_center.city.title
        }
      }

      // For regular users, use the user's city
      return state.city ? {
        id: state.city.id.toString(),
        documentId: state.city.documentId,
        name: state.city.name,
        label: state.city.name
      } : null
    },

    selectedCityLabel: (state) => {
      // For content managers and admin managers, use the resource center's city
      if ((state.role === 'content-manager' || state.role === 'admin-manager') && state.user?.resource_center?.city?.title) {
        return state.user.resource_center.city.title
      }
      // For regular users, use the user's city
      return state.city?.name || null
    },

    canSelectCity: (state) => {
      // Content managers and admin managers cannot select city - they use resource center city
      return state.role !== 'content-manager' && state.role !== 'admin-manager'
    },

    userResourceCenterDocumentId: (state) => {
      // For content managers and admin managers, return their resource center's documentId
      if ((state.role === 'content-manager' || state.role === 'admin-manager') && state.user?.resource_center?.documentId) {
        return state.user.resource_center.documentId
      }
      return null
    },

    userCityTitle: (state) => {
      // For content managers and admin managers, use the resource center's city title
      if ((state.role === 'content-manager' || state.role === 'admin-manager') && state.user?.resource_center?.city?.title) {
        return state.user.resource_center.city.title
      }
      // For regular users, use the user's city name
      return state.city?.name || null
    },
  },

  actions: {
    setAuthenticatedUser(user: StrapiUser, isAuthenticated: boolean = true) {
      this.user = user
      this.isAuthenticated = isAuthenticated

      // Map role types from backend to frontend
      if (user.role?.type === 'content_manager') {
        this.role = 'content-manager'
      } else if (user.role?.type === 'admin_manager') {
        this.role = 'admin-manager'
      } else if (user.role?.name === 'Organization') {
        this.role = 'organization'
      } else {
        this.role = null
      }

      // Preserve existing city if user from API doesn't have city data
      if (user.city) {
        this.city = user.city
      }

      cookieUtils.setUserData(this.role, this.city)
    },

    async login(identifier: string, google_token: string) {
      try {
        this.isLoading = true

        const response = await authApiClient.post('/auth/local', {
          identifier,
          google_token,
        })

        // Check if the response indicates failure (captcha error, etc.)
        if (response.data && response.data.success === false) {
          throw new Error(response.data.message || 'Login failed')
        }

        return response.data
      } catch (error) {
        const apiError = error as ApiError
        let message = 'Login failed'
        let errorId = null


        // Check if it's an axios error with response data first
        if (apiError.response?.data) {
          // Handle the specific nested structure: {data: null, error: {status: 400, details: {...}}}
          const responseData = apiError.response.data

          // Check if error is nested under response.data.error.details
          if (responseData?.error?.details && typeof responseData.error.details === 'object' && responseData.error.details !== null) {
            const errorDetails = responseData.error.details as Record<string, unknown>
            if (errorDetails.id && errorDetails.message) {
              errorId = errorDetails.id as string
              message = errorDetails.message as string
            }
          }
          // Check if details are at root level under response.data
          else if (responseData?.details && typeof responseData.details === 'object' && responseData.details !== null) {
            const details = responseData.details as Record<string, unknown>
            if (details.id && details.message) {
              errorId = details.id as string
              message = details.message as string
            }
          }
          // Check if error has message at response.data.error.message
          else if (responseData?.error?.message) {
            message = responseData.error.message
          }
          // Fallback to generic error message
          else {
            message = apiError.message || 'Login failed'
          }
        } else if (error instanceof Error) {
          // Check if it's our custom error from success: false
          message = error.message
        } else {
          // Ultimate fallback
          message = apiError.message || 'Login failed'
        }

        // Create error with additional metadata for frontend handling
        const customError = new Error(message) as Error & { errorId?: string }
        customError.errorId = errorId || undefined
        throw customError
      } finally {
        this.isLoading = false
      }
    },

    async verifyCode(data: { code: string; phone?: string }) {
      try {
        this.isLoading = true

        const response = await authApiClient.post('/auth/verify', {
          otp_token: data.code,
          phone: data.phone,
        })

        const responseData = response.data

        if (responseData.success === false) {
          throw new Error('Invalid verification code')
        }

        if (responseData.jwt) {
          cookieUtils.setCookie('jwtTokenLocal', responseData.jwt, 7)
        }

        if (responseData.user) {
          this.setAuthenticatedUser(responseData.user as StrapiUser)
        } else if (responseData.jwt) {
          try {
            await new Promise((resolve) => setTimeout(resolve, 100))
            await this.fetchCurrentUser()
          } catch {
            this.isAuthenticated = true
          }
        }

        if (responseData.jwt && !this.isAuthenticated) {
          this.isAuthenticated = true
        }

        // Log device information after successful authentication
        // This is non-blocking and won't affect the login flow
        if (responseData.jwt) {
          await deviceLogsService.logDeviceLogin()
        }

        return responseData
      } catch (error) {
        const apiError = error as ApiError
        const message =
          apiError.response?.data?.error?.message || apiError.message || 'Verification failed'
        throw new Error(message)
      } finally {
        this.isLoading = false
      }
    },

    async register(username: string, email: string, password: string) {
      try {
        this.isLoading = true

        const response = await authApiClient.post('/auth/local/register', {
          username,
          email,
          password,
        })

        const data = response.data

        if (data.jwt) {
          cookieUtils.setCookie('jwtTokenLocal', data.jwt, 7)
        }

        if (data.user) {
          this.setAuthenticatedUser(data.user as StrapiUser)
        }

        return data
      } catch (error) {
        const apiError = error as ApiError
        const message =
          apiError.response?.data?.error?.message || apiError.message || 'Registration failed'
        throw new Error(message)
      } finally {
        this.isLoading = false
      }
    },

    async registerOrganization(payload: {
      data: {
        first_name: string
        last_name: string
        personal_phone_number: string
        user_designation: string
        org_name: string
        username: string
        org_phone_number: string
        secondary_phone?: string
        email: string
        password: string
        confirm_password: string
        activity_type: string
        org_registration_doc?: number
        organization_goals: Record<string, unknown>
        social_links: Record<string, unknown>[]
        website?: string
        focus_areas: string[]
        bio?: string
        cover_color: string
        city: string | number | Record<string, unknown> | null
        address: string
        organization_establishment_date?: string | null
        registration_expiration_date?: string | null
        google_token: string
      }
    }) {
      try {
        this.isLoading = true

        console.log('🔐 AUTH STORE - registerOrganization payload:', payload)
        console.log('🔐 AUTH STORE - reCAPTCHA token details:', {
          token: payload.data.google_token ? `${payload.data.google_token.substring(0, 50)}...` : null,
          fullToken: payload.data.google_token,
          tokenLength: payload.data.google_token ? payload.data.google_token.length : 0
        })

        // Validate required fields
        const requiredFields = ['first_name', 'last_name', 'personal_phone_number', 'user_designation', 'org_name', 'username', 'email', 'password', 'google_token']
        const missingFields = requiredFields.filter(field => !payload.data[field as keyof typeof payload.data])

        if (missingFields.length > 0) {
          console.error('🔐 AUTH STORE - Missing required fields:', missingFields)
          throw new Error(`Missing required registration data: ${missingFields.join(', ')}`)
        }

        const response = await authApiClient.post('/ngos-registration', payload)

        console.log('🔐 AUTH STORE - registerOrganization API Response Status:', response.status)
        console.log('🔐 AUTH STORE - registerOrganization API Response Data:', response.data)
        console.log('🔐 AUTH STORE - registerOrganization API Response Headers:', response.headers)

        // Check if the response indicates failure
        if (response.data && response.data.success === false) {
          console.error('🔐 AUTH STORE - registerOrganization API returned success: false')
          throw new Error(response.data.message || 'Registration failed')
        }

        return response.data
      } catch (error) {
        console.error('🔐 AUTH STORE - registerOrganization Error:', error)

        const apiError = error as ApiError

        if (apiError.response) {
          console.error('🔐 AUTH STORE - registerOrganization Error Response Status:', apiError.response.status)
          console.error('🔐 AUTH STORE - registerOrganization Error Response Data:', apiError.response.data)
        }

        let message = 'Registration failed'
        let errorId = null

        // Use same error handling pattern as login
        if (apiError.response?.data) {
          const responseData = apiError.response.data

          if (responseData?.error?.details && typeof responseData.error.details === 'object' && responseData.error.details !== null) {
            const errorDetails = responseData.error.details as Record<string, unknown>
            if (errorDetails.id && errorDetails.message) {
              errorId = errorDetails.id as string
              message = errorDetails.message as string
            }
          } else if (responseData?.details && typeof responseData.details === 'object' && responseData.details !== null) {
            const details = responseData.details as Record<string, unknown>
            if (details.id && details.message) {
              errorId = details.id as string
              message = details.message as string
            }
          } else if (responseData?.error?.message) {
            message = responseData.error.message
          } else {
            message = apiError.message || 'Registration failed'
          }
        } else if (error instanceof Error) {
          message = error.message
        } else {
          message = apiError.message || 'Registration failed'
        }

        const customError = new Error(message) as Error & { errorId?: string }
        customError.errorId = errorId || undefined
        throw customError
      } finally {
        this.isLoading = false
      }
    },

    async registerPhone(payload: {
      identifier: string
      google_token: string
      privacy_consent: boolean
      terms_consent: boolean
      marketing_consent: boolean
    }) {
      try {
        this.isLoading = true

        console.log('🔐 AUTH STORE - registerPhone payload:', payload)
        console.log('🔐 AUTH STORE - reCAPTCHA token details:', {
          token: payload.google_token ? `${payload.google_token.substring(0, 50)}...` : null,
          fullToken: payload.google_token,
          tokenLength: payload.google_token ? payload.google_token.length : 0
        })

        // Validate payload before sending
        if (!payload.identifier || !payload.google_token) {
          console.error('🔐 AUTH STORE - Missing required data:', {
            hasIdentifier: !!payload.identifier,
            hasGoogleToken: !!payload.google_token,
            identifier: payload.identifier,
            googleTokenLength: payload.google_token ? payload.google_token.length : 0
          })
          throw new Error('Missing required registration data')
        }

        const response = await authApiClient.post('/auth/register', payload)

        console.log('🔐 AUTH STORE - registerPhone API Response Status:', response.status)
        console.log('🔐 AUTH STORE - registerPhone API Response Data:', response.data)
        console.log('🔐 AUTH STORE - registerPhone API Response Headers:', response.headers)

        // Check if the response indicates failure
        if (response.data && response.data.success === false) {
          console.error('🔐 AUTH STORE - registerPhone API returned success: false')
          throw new Error(response.data.message || 'Registration failed')
        }

        return response.data
      } catch (error) {
        console.error('🔐 AUTH STORE - registerPhone Error:', error)

        const apiError = error as ApiError

        if (apiError.response) {
          console.error('🔐 AUTH STORE - registerPhone Error Response Status:', apiError.response.status)
          console.error('🔐 AUTH STORE - registerPhone Error Response Data:', apiError.response.data)
        }

        let message = 'Registration failed'
        let errorId = null

        // Use same error handling pattern as login
        if (apiError.response?.data) {
          const responseData = apiError.response.data

          if (responseData?.error?.details && typeof responseData.error.details === 'object' && responseData.error.details !== null) {
            const errorDetails = responseData.error.details as Record<string, unknown>
            if (errorDetails.id && errorDetails.message) {
              errorId = errorDetails.id as string
              message = errorDetails.message as string
            }
          } else if (responseData?.details && typeof responseData.details === 'object' && responseData.details !== null) {
            const details = responseData.details as Record<string, unknown>
            if (details.id && details.message) {
              errorId = details.id as string
              message = details.message as string
            }
          } else if (responseData?.error?.message) {
            message = responseData.error.message
          } else {
            message = apiError.message || 'Registration failed'
          }
        } else if (error instanceof Error) {
          message = error.message
        } else {
          message = apiError.message || 'Registration failed'
        }

        const customError = new Error(message) as Error & { errorId?: string }
        customError.errorId = errorId || undefined
        throw customError
      } finally {
        this.isLoading = false
      }
    },

    async fetchCurrentUser() {
      const token = cookieUtils.getJWTToken()

      try {
        this.isLoading = true

        if (!token) {
          this.user = null
          this.isAuthenticated = false
          return null
        }

        const response = await authApiClient.get('/users/me', {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        })

        const user = response.data
        this.setAuthenticatedUser(user as StrapiUser)

        return user
      } catch {
        this.user = null
        this.isAuthenticated = false
        this.role = null
        this.city = null
        return null
      } finally {
        this.isLoading = false
      }
    },

    clearAuth() {
      // Clear all authentication-related cookies
      cookieUtils.clearAllAuthCookies()
      this.user = null
      this.isAuthenticated = false
      this.isLoading = false
      this.role = null
      this.city = null
    },

    async waitForTokenOrFetch(maxRetries = 3, delay = 1000) {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        if (this.hasValidToken) {
          return await this.fetchCurrentUser()
        }

        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, delay))
        }
      }

      this.user = null
      this.isAuthenticated = false
      this.role = null
      this.city = null
      return null
    },

    async logout() {
      this.clearAuth()

      // Clear notification-related storage
      const { clearNotificationStorage } = await import('@/composables/useOneSignalRegistration')
      await clearNotificationStorage()

      // Import router dynamically to avoid circular dependency
      const { default: router } = await import('@/router')
      await router.push('/login')
    },

    async initializeAuth() {
      if (initializeAuthPromise) return initializeAuthPromise

      // State is already initialized from cookies in state factory
      initializeAuthPromise = (this.hasValidToken ? this.fetchCurrentUser() : this.waitForTokenOrFetch()).finally(() => {
        initializeAuthPromise = null
      })
      return initializeAuthPromise
    },

    selectCity(cityData: { id: string; documentId: string; label: string;[key: string]: unknown }) {
      if (this.role === 'content-manager') {
        return
      }

      const newCity = {
        id: parseInt(cityData.id),
        documentId: cityData.documentId,
        name: cityData.label
      }

      this.city = newCity
      cookieUtils.setUserData(this.role, newCity)
    },

  },
})
